/**
 * Region move preview (BON-448).
 *
 * Its own module rather than an addition to `regions.ts`: that file already sits
 * just under the 300-line cap, so growing it turns every future change to it
 * into a lint failure.
 */
import { apiFetch } from '../lib/apiFetch';

import { errorMessage } from './regions';

/** Why a move is refused. Mirrors the BON-443 database triggers. */
export interface MoveBlocker {
  kind: 'not_found' | 'parent_not_found' | 'self_parent' | 'cycle' | 'too_deep' | 'carries_people';
  message: string;
}

/** What a move would do, before doing it (BON-448). */
export interface MovePreview {
  regionId: string;
  regionName: string;
  parentId: string | null;
  parentName: string | null;
  subregionsMoving: number;
  venuesMoving: number;
  grantsAffected: number;
  /** Top-level region reporting this subtree now; null when already top level. */
  sourceRootName: string | null;
  /** Top-level region that will report it; null when moving to top level. */
  destinationRootName: string | null;
  /** Non-empty means the move is refused. */
  blockers: MoveBlocker[];
}

/** Preview a move. `parentId` null previews a move to top level. */
export async function previewRegionMove(id: string, parentId: string | null): Promise<MovePreview> {
  const query = parentId === null ? '' : `?parentId=${encodeURIComponent(parentId)}`;
  const res = await apiFetch(`/api/v1/regions/${id}/move-preview${query}`);
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to preview move: ${res.status}`));
  return (await res.json()) as MovePreview;
}
